import { useLayoutEffect, useState } from "react";
import type { RefObject } from "react";

/**
 * UX3010-04 (30.10.2026): the grid of a card follows the width the form really has — a page, a drawer, a phone, or a
 * desktop at 200 % zoom — not the width of the window. Three classes:
 * - «phone»: one field per row;
 * - «narrow» (a drawer, a small laptop, 200 % zoom — a screen of about 640–900 px): short fields two in a row,
 *   long ones (`l`, `xl`) on a row of their own;
 * - «wide»: the width class of the meta as a column of 24 (xs 4, s 6, m 8, l 12, xl 24).
 */
export type FormLayout = "phone" | "narrow" | "wide";

/** Section 5.1 of the UX spec: the column a width class takes on a wide form. */
export const WIDTH_SPAN: Record<string, number> = { xs: 4, s: 6, m: 8, l: 12, xl: 24 };

/** Below this width of the form itself every field is a row of its own. */
export const PHONE_MAX = 520;
/** Below this width short fields go two in a row and long ones take the whole row. */
export const NARROW_MAX = 820;

export function formLayout(width: number): FormLayout {
  if (width < PHONE_MAX) return "phone";
  if (width < NARROW_MAX) return "narrow";
  return "wide";
}

/** The column of a field (out of 24) by its width class and the class of the form. Unknown classes count as `l`. */
export function columnSpan(width: string | null | undefined, layout: FormLayout): number {
  const cls = width && width in WIDTH_SPAN ? width : "l";
  if (layout === "phone") return 24;
  if (layout === "narrow") return cls === "l" || cls === "xl" ? 24 : 12;
  return WIDTH_SPAN[cls];
}

/** Length of a text as the server counts it (code points, not UTF-16 halves): «Не длиннее 64 символов (сейчас 70)». */
export function textLength(value: unknown): number {
  return typeof value === "string" ? [...value].length : 0;
}

/** «Не длиннее 64 символов (сейчас 70): сократите текст» — the same words as the server (common/errors.py). */
export function tooLong(value: unknown, max: number | null | undefined): string | null {
  if (!max) return null;
  const length = textLength(value);
  return length > max ? `Не длиннее ${max} символов (сейчас ${length}): сократите текст` : null;
}

/**
 * The width of an element, kept up to date by a ResizeObserver. A hidden element (an inactive tab) reports 0 —
 * the last real width is kept, so switching tabs does not reflow the form twice. `null` until measured.
 */
export function useElementWidth(ref: RefObject<HTMLElement>): number | null {
  const [width, setWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (w: number) => {
      if (w > 0) setWidth((prev) => (prev !== null && Math.abs(prev - w) < 1 ? prev : Math.round(w)));
    };
    measure(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) measure(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}
